import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface Crumb {
  label: string;
  /** Absolute path for a NavLink; omit for the current (non-clickable) crumb. */
  to?: string;
}

interface BreadcrumbsState {
  crumbs: Crumb[];
  setCrumbs: (next: Crumb[]) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsState | undefined>(undefined);

export function BreadcrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
}

export function useBreadcrumbs(): Crumb[] {
  const ctx = useContext(BreadcrumbsContext);
  return ctx?.crumbs ?? [];
}

/**
 * Set the breadcrumb trail for the current screen. Pass an empty array to clear.
 * The effect cleans up on unmount so screens don't leak their crumbs to siblings.
 */
export function useSetBreadcrumbs(crumbs: Crumb[]): void {
  const ctx = useContext(BreadcrumbsContext);
  const setCrumbs = ctx?.setCrumbs;
  // Stringify to avoid effect re-fires when callers pass a new array with the same content.
  const serialized = JSON.stringify(crumbs);
  // `serialized` is the intentional dependency — it captures crumbs' content, so we
  // deliberately don't depend on the crumbs array identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useCallback(() => crumbs, [serialized]);
  useEffect(() => {
    if (!setCrumbs) return;
    setCrumbs(stable());
    return () => setCrumbs([]);
  }, [setCrumbs, serialized, stable]);
}
